import { Alert, Autocomplete, Loader, Select, Skeleton, TextInput } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "@hooks/useTranslation";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type { CatalogProvider } from "@hooks/flow/useModelCatalog";
import { useDeprecations } from "@hooks/flow/useDeprecations";
import { AdminPermissions } from "@config/permissions";

interface ProviderModelSelectProps {
    isLoadingProviders: boolean;
    isLoadingModels?: boolean;
    providers: CatalogProvider[];
    /** Suggestions only: models this provider was recently called with. */
    models: string[];
    /** Provider key, as the step stores it. */
    selectedProvider: string;
    modelName: string;
    /** Shown when the model is empty, e.g. the step model an agent inherits. */
    modelPlaceholder?: string;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
}

/**
 * Provider + model of a flow step. Providers are the runtime's supported
 * adapters; the model is free text (any id the provider accepts, e.g. an Azure
 * deployment name) with recently used models as suggestions. A stored provider
 * key the catalog no longer lists stays visible rather than being dropped.
 */
export const ProviderModelSelect = ({
    isLoadingProviders,
    isLoadingModels,
    providers,
    models,
    selectedProvider,
    modelName,
    modelPlaceholder,
    onProviderChange,
    onModelChange,
}: ProviderModelSelectProps) => {
    const { t } = useTranslation();
    const { canCreateDoctype, hasPermission } = useAdminPermissions();
    const { find } = useDeprecations(hasPermission(AdminPermissions.PROVIDER_VIEW));
    // Warn, never block: the step keeps working until the provider's retire date.
    const deprecation = selectedProvider ? find(selectedProvider, modelName || modelPlaceholder || "") : null;
    const warning = deprecation && (
        <Alert color={deprecation.status === "deprecated" ? "yellow" : deprecation.status === "retiring" ? "orange" : "red"}
               icon={<IconAlertTriangle size={16} />} p="xs">
            {t(`admin.flowConfigs.deprecation.${deprecation.status}`, { date: deprecation.retire_on, count: deprecation.days_left })}
            {deprecation.replacement ? ` ${t("admin.flowConfigs.deprecation.replaceWith", { model: deprecation.replacement })}` : ""}
        </Alert>
    );

    if (isLoadingProviders) {
        return (
            <>
                <Skeleton height={56} />
                <Skeleton height={56} />
            </>
        );
    }

    // Read-only: can edit a doctype but not pick models; show the stored values.
    if (!canCreateDoctype) {
        return (
            <>
                <TextInput label={t("admin.flowConfigs.labels.provider")} value={selectedProvider} disabled readOnly />
                <TextInput label={t("admin.table.columns.model")} value={modelName} placeholder={modelPlaceholder} disabled readOnly />
                {warning}
            </>
        );
    }

    const providerData = providers.map((p) => ({ value: p.key, label: p.label }));
    if (selectedProvider && !providers.some((p) => p.key === selectedProvider)) {
        providerData.push({ value: selectedProvider, label: selectedProvider });
    }

    return (
        <>
            <Select
                label={t("admin.flowConfigs.labels.provider")}
                value={selectedProvider || null}
                onChange={(value) => onProviderChange(value || "")}
                data={providerData}
                allowDeselect={false}
                comboboxProps={{ withinPortal: false }}
            />
            <Autocomplete
                label={t("admin.table.columns.model")}
                description={t("admin.flowConfigs.labels.modelHint")}
                value={modelName}
                placeholder={modelPlaceholder}
                onChange={onModelChange}
                data={models}
                disabled={!selectedProvider}
                rightSection={isLoadingModels ? <Loader size={16} /> : undefined}
                comboboxProps={{ withinPortal: false }}
            />
            {warning}
        </>
    );
};
